import React from 'react';
import type { TotalesCaptura, VistaTienda } from '../types/metas.types';
import { textoEstadoCaptura } from '../utils/estado';
import { momentoMx, nombrePropio, pesos, pesosEnteros } from '../utils/formato';
import { indicadorListos, indicadorMetaTienda, indicadorPorAsignar, notaSinComparar, TonoIndicador } from '../utils/kpis';
import { BarraAvance, pctAvance } from './ui/BarraAvance';
import { CLASE_BOTON_TEXTO, CLASE_PANEL, cx } from './ui/clases';
import { KpiCard } from './ui/KpiCard';
import { PasosEstado } from './ui/PasosEstado';
import { EstadoPill, Pill } from './ui/Pill';

interface Props {
  vista: VistaTienda;
  /** Los totales EN VIVO (lo que hay en pantalla, guardado o no). */
  totales: TotalesCaptura;
  /** Tolerancia de la suma contra la meta de tienda (`parametros.toleranciaSuma`). */
  tolerancia?: number;
  /** Versión de teléfono (tarjeta de resumen del artboard `MovilCaptura`). */
  movil?: boolean;
  /** Lo que va al pie de la tarjeta del teléfono («Copiar semana anterior»). */
  children?: React.ReactNode;
  /** Se inyecta en las pruebas para fijar «hoy». */
  ahora?: number;
  /**
   * La vista es de la tienda que se está dejando (carga otra) o se recarga: el
   * resumen se marca «Cargando…» y se atenúa, para no leer las cifras viejas
   * como las de la tienda elegida.
   */
  cargando?: boolean;
  /**
   * «Repartir según venta promedio» bajo POR ASIGNAR (diseño `Main`). La
   * pantalla lo pasa sólo cuando falta algo y la tienda se puede capturar.
   */
  repartir?: { onClick: () => void; ocupado?: boolean; deshabilitado?: boolean };
  /**
   * «Repartir en partes iguales» (ronda F, 23-oct-2026, decisión del usuario),
   * junto al de arriba. La pantalla lo pasa con las mismas condiciones y,
   * además, sólo si la API lo anuncia (`vista.capacidades.repartirIguales`).
   */
  repartirIguales?: { onClick: () => void; ocupado?: boolean; deshabilitado?: boolean };
  /** La captura lleva el renglón de la Casa (ronda F): «N colaboradores · Casa». */
  conCasa?: boolean;
  /**
   * La tienda captura (el gerente, fase B): cuatro pasos y «Te la envió el
   * gerente». Por omisión apagado (22-oct-2026, decisión del usuario): los tres
   * pasos del supervisor y el nombre de siempre de cada estado.
   */
  capturaTienda?: boolean;
}

/** La marca «Cargando…» del resumen mientras llega la tienda elegida. */
const MarcaCargando = () => (
  <span
    role="status"
    data-testid="resumen-cargando"
    className="inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-metas-neutro-fondo px-2.5 text-xs font-semibold text-metas-neutro"
  >
    <i className="pi pi-spin pi-spinner text-[11px]" aria-hidden="true" />
    Cargando…
  </span>
);

/** El renglón chico bajo el paso actual: «Guardado hoy 10:42», «Enviada ayer 18:05»… */
export function detallePaso(vista: VistaTienda, ahora?: number): string | undefined {
  const e = vista.estado;
  const cuando = (v: string | null | undefined) => momentoMx(v, ahora);
  switch (e.estado) {
    case 'borrador':
      return e.actualizadoEn ? `Guardado ${cuando(e.actualizadoEn)}` : undefined;
    case 'revision':
      // Desde cuándo la tiene el supervisor (21-oct-2026): la mandó el gerente.
      return e.enviadoGerenteEn ? `La envió el gerente ${cuando(e.enviadoGerenteEn)}` : undefined;
    case 'enviada':
      return e.enviadoEn ? cuando(e.enviadoEn) : undefined;
    case 'rechazada':
    case 'autorizada':
      return e.resueltoEn ? cuando(e.resueltoEn) : undefined;
    default:
      return undefined;
  }
}

/** «2º envío» cuando la tienda ya se envió más de una vez (volvió de un rechazo). */
export const textoEnvio = (n: number | null | undefined): string | null => (n && n >= 2 ? `${n}º envío` : null);

const TONO_MOSAICO: Record<TonoIndicador, { caja: string; etiqueta: string; valor: string }> = {
  error: { caja: 'bg-metas-error-suave', etiqueta: 'text-metas-error-oscuro', valor: 'text-metas-error' },
  aviso: { caja: 'bg-metas-aviso-suave', etiqueta: 'text-metas-aviso-oscuro', valor: 'text-metas-aviso-oscuro' },
  exito: { caja: 'bg-metas-exito-fondo', etiqueta: 'text-metas-exito', valor: 'text-metas-exito' },
  normal: { caja: 'bg-metas-neutro-fondo', etiqueta: 'text-metas-texto-2', valor: 'text-metas-texto' },
};

const Mosaico: React.FC<{ etiqueta: string; valor: string; tono: TonoIndicador; title?: string }> = ({ etiqueta, valor, tono, title }) => {
  const t = TONO_MOSAICO[tono];
  return (
    <div role="group" aria-label={etiqueta} title={title} className={cx('flex min-w-0 flex-col gap-0.5 rounded-metas-control px-3 py-2.5', t.caja)}>
      <span className={cx('text-[11px] font-semibold uppercase tracking-[.04em]', t.etiqueta)}>{etiqueta}</span>
      <span className={cx('num text-base font-semibold', t.valor)} data-testid="kpi-valor">
        {valor}
      </span>
    </div>
  );
};

/**
 * La cabecera y los indicadores de la tienda abierta (panel «Resumen de la
 * tienda» del diseño): nombre y estado, supervisor, regional y cuántos
 * colaboradores; los cuatro pasos; y META DE TIENDA, SUMA CAPTURADA (con su
 * barra), POR ASIGNAR y COLABORADORES LISTOS. Todo se recalcula en cada tecla.
 *
 * En teléfono es la tarjeta del artboard `MovilCaptura`: la suma grande con su
 * barra y dos mosaicos (POR ASIGNAR, LISTOS).
 */
export const ResumenCaptura: React.FC<Props> = ({
  vista,
  totales,
  tolerancia = 0.02,
  movil,
  children,
  ahora,
  cargando = false,
  repartir,
  repartirIguales,
  conCasa = false,
  capturaTienda = false,
}) => {
  const metaT = indicadorMetaTienda(vista.metaTienda);
  // Sin meta comparable, POR ASIGNAR dice por qué (incompleta, con IVA): decía «Sin
  // meta de tienda» junto a «$274,624 · Sólo 6 de 7 días cargados» (21-oct-2026).
  const porAsignar = indicadorPorAsignar(totales.suma, metaT.comparable, tolerancia, notaSinComparar(vista.metaTienda));
  const listos = indicadorListos(totales);
  const fraccion = metaT.comparable ? totales.suma / metaT.comparable : null;
  const envio = textoEnvio(vista.estado.numEnvio);
  const titulo = `${vista.tienda.chain} ${vista.tienda.num} · ${vista.tienda.nombre}`;
  // La Casa no es una persona (ronda F): no cuenta en «N colaboradores»; se dice aparte.
  const colaboradores = `${totales.total === 1 ? '1 colaborador' : `${totales.total} colaboradores`}${conCasa ? ' · Casa' : ''}`;
  const detalle = detallePaso(vista, ahora);
  // «Incluye $X de la Casa»: su meta va en la suma (ronda F).
  const notaCasa = totales.metaCasa ? `Incluye ${pesos(totales.metaCasa)} de la Casa` : null;
  const unEnlace = (
    accion: { onClick: () => void; ocupado?: boolean; deshabilitado?: boolean } | undefined,
    texto: string,
    clase: string,
  ) =>
    accion ? (
      <button
        type="button"
        className={clase}
        onClick={accion.onClick}
        disabled={accion.deshabilitado || accion.ocupado}
        aria-busy={accion.ocupado || undefined}
      >
        {accion.ocupado && <i className="pi pi-spin pi-spinner text-[11px]" aria-hidden="true" />}
        {texto}
      </button>
    ) : null;
  /** Los dos enlaces bajo «Por asignar»: según venta promedio y, si la API lo sabe hacer, en partes iguales. */
  const enlacesRepartir = (clase: string) =>
    repartir || repartirIguales ? (
      <>
        {unEnlace(repartir, 'Repartir según venta promedio', clase)}
        {unEnlace(repartirIguales, 'Repartir en partes iguales', clase)}
      </>
    ) : null;

  const personas = (
    <>
      Supervisor <b className="font-semibold text-metas-texto">{nombrePropio(vista.tienda.supervisor) || '—'}</b> · Regional{' '}
      <b className="font-semibold text-metas-texto">{nombrePropio(vista.tienda.regional) || '—'}</b> · {colaboradores}
    </>
  );

  if (movil) {
    return (
      <section
        aria-label="Resumen de la tienda"
        aria-busy={cargando || undefined}
        className={cx(CLASE_PANEL, 'flex flex-col gap-3 p-4', cargando && 'opacity-60')}
        data-testid="resumen-captura"
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="m-0 min-w-0 truncate text-lg font-semibold">{titulo}</h2>
          <span className="flex shrink-0 items-center gap-1.5">
            {cargando ? (
              <MarcaCargando />
            ) : (
              <>
                {envio && <Pill tono="info">{envio}</Pill>}
                <EstadoPill estado={vista.estado.estado} texto={textoEstadoCaptura(vista.estado.estado, !!vista.estado.devolucion, capturaTienda)} />
              </>
            )}
          </span>
        </div>
        <PasosEstado estado={vista.estado.estado} detalle={detalle} compacto capturaTienda={capturaTienda} />
        <div className="text-xs text-metas-texto-2">{personas}</div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="num text-xl font-semibold" aria-label={`Suma capturada ${pesosEnteros(totales.suma)}`}>
              {pesosEnteros(totales.suma)}
            </span>
            <span className="num text-[13px] text-metas-texto-2">
              {metaT.comparable !== null ? `de ${metaT.valor} · ${pctAvance(fraccion)}` : `Meta de tienda: ${metaT.nota.toLowerCase()}`}
            </span>
          </div>
          {metaT.comparable !== null && (
            <BarraAvance fraccion={fraccion} etiqueta="Suma capturada contra la meta de tienda" mostrarPct={false} alto={8} />
          )}
          {notaCasa && (
            <span className="text-xs text-metas-texto-2" data-testid="suma-casa">
              {notaCasa}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Mosaico etiqueta="Por asignar" valor={porAsignar.valor} tono={porAsignar.tono} title={porAsignar.nota} />
          <Mosaico
            etiqueta="Listos"
            valor={`${listos.listos} de ${listos.de}`}
            tono={listos.pendientes > 0 ? 'aviso' : 'exito'}
            title={listos.nota}
          />
        </div>
        {(repartir || repartirIguales) && (
          // Dos enlaces de 44 px (ronda F): uno por renglón si no caben juntos a 390 px.
          <div className="-mt-1 flex flex-wrap gap-x-4" data-testid="enlaces-repartir">
            {enlacesRepartir(cx(CLASE_BOTON_TEXTO, 'self-start px-0 text-[13px] hover:bg-transparent hover:underline'))}
          </div>
        )}
        {children}
      </section>
    );
  }

  const borde = 'border-metas-borde-suave';
  return (
    <section
      aria-label="Resumen de la tienda"
      aria-busy={cargando || undefined}
      className={cx(CLASE_PANEL, cargando && 'opacity-60')}
      data-testid="resumen-captura"
    >
      <div className={cx('flex flex-wrap items-center justify-between gap-4 border-b px-6 py-5', borde)}>
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="m-0 text-metas-h2">{titulo}</h2>
            {cargando ? (
              <MarcaCargando />
            ) : (
              <>
                <EstadoPill estado={vista.estado.estado} texto={textoEstadoCaptura(vista.estado.estado, !!vista.estado.devolucion, capturaTienda)} />
                {envio && <Pill tono="info">{envio}</Pill>}
              </>
            )}
          </div>
          <div className="text-[13px] text-metas-texto-2">{personas}</div>
        </div>
        <PasosEstado estado={vista.estado.estado} detalle={detalle} capturaTienda={capturaTienda} />
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4">
        <KpiCard etiqueta="Meta de tienda" valor={metaT.valor} detalle={metaT.nota} tono={metaT.tono} />
        <KpiCard
          etiqueta="Suma capturada"
          valor={pesosEnteros(totales.suma)}
          className={cx('border-l', borde)}
          detalle={
            metaT.comparable !== null ? (
              notaCasa ? (
                <div className="flex flex-col gap-1">
                  <BarraAvance fraccion={fraccion} etiqueta="Suma capturada contra la meta de tienda" />
                  <span data-testid="suma-casa">{notaCasa}</span>
                </div>
              ) : (
                <BarraAvance fraccion={fraccion} etiqueta="Suma capturada contra la meta de tienda" />
              )
            ) : (
              `${totales.conMeta} con meta${notaCasa ? ` · ${notaCasa.charAt(0).toLowerCase()}${notaCasa.slice(1)}` : ''}`
            )
          }
        />
        <KpiCard
          etiqueta="Por asignar"
          valor={porAsignar.valor}
          tono={porAsignar.tono}
          detalle={
            repartir || repartirIguales ? (
              <div className="flex flex-col items-start gap-1" data-testid="enlaces-repartir">
                <span>{porAsignar.nota}</span>
                {/* En pantalla táctil (tableta de 1024 o más), 44 px de alto cada uno. */}
                {enlacesRepartir(
                  'metas-foco inline-flex items-center gap-1.5 rounded-sm border-0 bg-transparent p-0 text-left text-xs font-semibold text-metas-primario hover:underline disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-metas-tactil',
                )}
              </div>
            ) : (
              porAsignar.nota
            )
          }
          className={cx('border-t xl:border-l xl:border-t-0', borde)}
        />
        <KpiCard
          etiqueta="Colaboradores listos"
          valor={listos.listos}
          complemento={`de ${listos.de}`}
          detalle={
            <span className={cx('font-semibold', listos.tono === 'aviso' ? 'text-metas-aviso' : 'text-metas-exito')}>{listos.nota}</span>
          }
          className={cx('border-l border-t xl:border-t-0', borde)}
        />
      </div>
    </section>
  );
};

export default ResumenCaptura;
